<template>
  <div class="relative surface rounded noselect mb-2 " :class="{
    'deleted': item.deleted,
    'warning': item.is_empty,
    'not-active': item.changed || !allowChange || !item.is_active,
    'highlighted-item': item._isHighlighted
  }">

    <div v-if="lockElement" class="lock-overlay"></div>

  
    <div v-if="isSaving" class="loading-overlay">
              <div class="loading-spinner"></div>
            </div>

    <!-- Item header -->
    <item-header 
      :item="item" 
      :locked="lockElement"
      :title="(item.name !== 'schedule name') ? item.name : item.schedule_ration.name" 
      :status="item.status"
      :groups="item.schedule_groups" 
      @updated="onChange" 
      @on-toggle="toggle"
      @highlight-related="$emit('highlight-related', $event)"
      @unhighlight-related="$emit('unhighlight-related', $event)" />


    <!-- Extend retion info -->
    <div v-if="toggled" class="more-info">

      <!-- List of ration groups -->
      <group-list 
        ref="groupList"
        :id="item.id" 
        :is-locked="lockElement || isSaving"
        :groups="item.schedule_groups" 
        @on-update="updateAndSaveGroups"
        @on-change="updateGroups" 
        @on-group-add="openAddGroupDialog" 
        @on-add="replaceAndSaveGroups"
        @on-save-direct="saveChangesDirectly" />

      <!-- Info about ration type, ingredients and mixer -->
      <info-wrapper :item="item" @on-change="saveChanges" />

    </div>
  </div>
</template>

<script>
import api from '@/api'

import ItemHeader from './item-parts/ItemHeader.vue'
import GroupList from './item-parts/GroupList.vue'
import InfoWrapper from './item-parts/InfoWrapper.vue'

export default {
  name: 'ListItem',

  components: {
    ItemHeader,
    GroupList,
    InfoWrapper
  },

  props: {
    item: Object,
    baseWeightPerAnimal: Number,
    isLoadingRationWeight: Boolean
  },

  data: () => ({
    toggled: false,
    showAddGroupDialog: false,
    hasChanges: false,
    groupInitialWeights: {},
    initialWeightPerHead: 0,
    isSaving: false
  }),

  methods: {
    toggle (state) {
      this.toggled = state
    },

    async replaceAndSaveGroups (groups) {
      this.isSaving = true
      this.item.schedule_groups = groups
      this.item.changed = true

      try {
        const response = await api.schedule.updateById(this.item.id, this.item)

        if (response.data && response.data.schedule_groups) {
          this.item.schedule_groups = response.data.schedule_groups
        }

        this.initializeGroupWeights()

        this.$emit('updated')
        this.$toasted.show(this.$t('info.changed'))
      } catch (error) {
        console.error('Save failed:', error)
        this.$toasted.error(this.$t('info.error_occurred'))
      } finally {
        this.item.changed = false
        this.isSaving = false
      }
    },

    updateGroups (groups) {
      this.item.schedule_groups = groups
      this.sortGroups()
      this.hasChanges = true
    },

    initializeGroupWeights () {
      this.groupInitialWeights = {}

      this.item.schedule_groups.forEach(group => {
        if (group.coefficient > 0 && group.animal_count > 0 && group.plan_weight > 0) {
          this.groupInitialWeights[group.id] = (group.plan_weight * 100) / (group.animal_count * group.coefficient)
        } else if (this.baseWeightPerAnimal > 0) {
          this.groupInitialWeights[group.id] = this.baseWeightPerAnimal * this.item.coefficient / 100
        }
      })
    },

    async updateAndSaveGroups (groups) {
      this.isSaving = true

      groups.forEach(group => {
        if (group.animal_count > 0) {
          if (!this.groupInitialWeights[group.id] && group.coefficient > 0 && group.plan_weight > 0) {
            this.groupInitialWeights[group.id] = (group.plan_weight * 100) / (group.animal_count * group.coefficient)
          }

          const perOneWeight = this.groupInitialWeights[group.id]

          if (perOneWeight) {
            const baseWeight = group.animal_count * perOneWeight
            group.coefficient = (group.plan_weight / baseWeight) * 100
          }
        } else {
          group.coefficient = 0
        }
      })

      this.item.schedule_groups = groups
      this.item.changed = true

      try {
        await this.updateSchedule(this.item.id, this.item)
        this.$toasted.show(this.$t('info.changed'))
      } catch (error) {
        console.error('Update failed:', error)
        this.$toasted.error(this.$t('info.error_occurred'))
      } finally {
        this.isSaving = false
      }
    },

    async saveChangesDirectly (groupData) {
      if (!groupData || !groupData.groupId) return

      const groupIndex = this.item.schedule_groups.findIndex(g => g.id === groupData.groupId)

      if (groupIndex === -1) return

      this.isSaving = true

      try {
        this.$set(this.item.schedule_groups[groupIndex], 'plan_weight', groupData.plan_weight)
        this.$set(this.item.schedule_groups[groupIndex], 'coefficient', groupData.coefficient)

        const freshPayload = this.buildFreshPayload()

        await api.schedule.updateById(this.item.id, freshPayload)

        this.$toasted.show(this.$t('info.changed'))

        this.$forceUpdate()

        if (this.$refs.groupList) {
          this.$refs.groupList.$forceUpdate()
        }

        await new Promise(resolve => setTimeout(resolve, 300))

        this.$emit('updated')
      } catch (error) {
        console.error('Save failed:', error)
        this.$toasted.error(this.$t('info.error_occurred'))
      } finally {
        this.isSaving = false
      }
    },

    buildFreshPayload () {
      const groupList = this.$refs.groupList || this.$children.find(c => c.$options.name === 'GroupList')
      const freshGroups = groupList ? groupList.items : this.item.schedule_groups

      return {
        ...this.item,
        schedule_groups: freshGroups.map(g => ({
          id: g.id,
          group: g.group,
          animal_count: g.animal_count,
          plan_weight: g.plan_weight,
          coefficient: g.coefficient,
          ordering: g.ordering,
          deleted: g.deleted || false,
          fact_weight: g.fact_weight,
          uploaded_at: g.uploaded_at
        }))
      }
    },

    sortGroups () {
      this.item.schedule_groups.forEach((element, index) => {
        element.ordering = index + 1
      })
    },

    async saveChanges (options = {}) {
      await this.updateSchedule(this.item.id, this.item, options.reloadAfter)
    },

    async updateSchedule (id, item, reloadAfter = false) {
      if (!item.deleted &&
        !item.schedule_mixer.deleted && !item.schedule_ration.deleted) {
        await api.schedule.updateById(id, item)

        if (reloadAfter) {
          const { data } = await api.schedule.getById(id)
          if (data.schedule_ration) this.$set(this.item, 'schedule_ration', data.schedule_ration)
          if (data.schedule_groups) {
            this.$set(this.item, 'schedule_groups', data.schedule_groups)
            this.initializeGroupWeights()
          }
          this.$emit('updated')
        }

        this.hasChanges = false
      } else {
        console.warn('Update skipped due to validation')
      }
    },

    openAddGroupDialog () {
      this.showAddGroupDialog = true
    },

    closeAddGroupDialog () {
      this.showAddGroupDialog = false
    },

    onChange () {
      this.$emit('updated')
    }
  },

  mounted () {
    this.initializeGroupWeights()
  },

  computed: {
    hasElements () {
      return !!this.item.schedule_groups.length
    },

    lockElement () {
      return this.isLoadingRationWeight ||
        this.item.deleted ||
        this.item.changed ||
        this.item.schedule_loading.deleted ||
        !this.allowChange ||
        this.item.status === 'loaded'
    },

    allowChange () {
      return this.hasPermission('schedule', 'change')
    }
  },

  watch: {
    baseWeightPerAnimal () {
      this.initializeGroupWeights()
    },

    'item.coefficient' () {
      this.initializeGroupWeights()
    },

    hasChanges (value) {
      if (value) {
        this.saveChanges()
      }
    },

    'item.schedule_groups': {
      handler () {
        this.initializeGroupWeights()
      }
    }
  }
}
</script>

<style scoped>
.deleted {
  background-color: #FFC0B3;
}

.warning {
  border-color: #F9CD13;
}

.not-active {
  background-color: #e5e5e5;
}

.highlighted-item {
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
  transition: all 0.3s ease;
}

.lock-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 45px;
  bottom: 0;
  z-index: 1;
  opacity: .05;
  background: var(--color-white);
  border-radius: 1rem;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
    background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  color: white;
}

.loading-spinner {
  width: 4rem;
  height: 4rem;
  border: 0.4rem solid #1da643;
  border-top: 0.4rem solid #1da644af;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.6rem;
}


.more-info {
  border-top: 5px solid #E5E5E5;
}
</style>
